/**
 * Wall-clock date helpers for Balancio.
 * DB stores naive local business times (Y-m-d H:i:s). Never convert via toISOString()
 * (that shifts by the browser timezone and desyncs filters/display from the API).
 */

const pad = (n: number) => String(n).padStart(2, '0');

/** Format a Date as local `YYYY-MM-DD HH:mm:ss` for API filters/payloads. */
export function formatLocalDateTime(date: Date, time = 'keep'): string {
  const y = date.getFullYear();
  const m = pad(date.getMonth() + 1);
  const d = pad(date.getDate());
  if (time === 'start') return `${y}-${m}-${d} 00:00:00`;
  if (time === 'end') return `${y}-${m}-${d} 23:59:59`;
  return `${y}-${m}-${d} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

/** Format a Date as local `YYYY-MM-DD`. */
export function formatLocalDate(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Format for datetime-local inputs (`YYYY-MM-DDTHH:mm`). */
export function formatLocalDateTimeInput(date: Date = new Date()): string {
  return `${formatLocalDate(date)}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/**
 * Parse a DB/API datetime as local wall-clock time.
 * Strips trailing Z / offset so UTC serialization does not shift the clock.
 */
export function parseDbDateTime(value?: string | null): Date | null {
  if (!value) return null;

  const normalized = String(value).trim().replace(' ', 'T');
  const match = normalized.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?$/i
  );

  if (match) {
    return new Date(
      Number(match[1]),
      Number(match[2]) - 1,
      Number(match[3]),
      Number(match[4] ?? 0),
      Number(match[5] ?? 0),
      Number(match[6] ?? 0)
    );
  }

  const fallback = new Date(value);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

/** Friendly display parts for a DB datetime. */
export function formatDbDateTimeDisplay(value?: string | null): { date: string; time: string } {
  const date = parseDbDateTime(value);
  if (!date) return { date: value ? 'Invalid Date' : 'N/A', time: value ? 'Invalid Time' : 'N/A' };

  return {
    date: date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }),
    time: date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }),
  };
}

/** Sanitize API errors so clients never see raw SQL / stack traces. */
export function sanitizeClientError(message?: string | null, fallback = 'Something went wrong. Please try again.'): string {
  if (!message) return fallback;
  const lower = message.toLowerCase();
  if (
    lower.includes('sqlstate') ||
    lower.includes('integrity constraint') ||
    lower.includes('duplicate entry') ||
    lower.includes('stack trace') ||
    lower.includes('sql:') ||
    message.includes('INSERT INTO') ||
    message.includes('UPDATE ')
  ) {
    if (lower.includes('duplicate entry')) {
      const named = message.match(/Duplicate entry '([^']+)'/i);
      if (named?.[1]) {
        return `"${named[1]}" is already in use. Choose a different name or select the existing one.`;
      }
      return 'That name is already in use. Choose a different name or select the existing one.';
    }
    return fallback;
  }
  return message;
}
